import React, { useState, useRef, useEffect } from 'react';
import { Mail, ArrowRight, RefreshCw, CheckCircle } from 'lucide-react';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerification = ({ email, onVerified, onBack, deliveryFailed = false }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      handleVerify(pasted);
    }
  };

  const handleVerify = async (fullCode) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      await api.post(`${BACKEND_URL}/api/auth/verify-email`, { email, code: codeStr });
      setVerified(true);
      setTimeout(() => onVerified(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await api.post(`${BACKEND_URL}/api/auth/resend-verification`, { email });
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafbfc] px-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
          <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafbfc] px-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-5">
            <Mail className="w-7 h-7 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
          <p className="text-sm text-gray-500">
            We sent a 6-digit verification code to<br />
            <span className="font-semibold text-gray-700">{email}</span>
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-6" data-testid="verification-code-inputs">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={i === 0 ? handlePaste : undefined}
              className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all ${
                error ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
              data-testid={`code-input-${i}`}
            />
          ))}
        </div>

        {deliveryFailed && (
          <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs leading-relaxed" data-testid="verification-delivery-warning">
            We couldn't send the code to your inbox just now. Hit <strong>Resend code</strong> below — if it still
            doesn't arrive, reply to our team and we'll verify you manually.
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm text-center mb-4" data-testid="verification-error">{error}</p>
        )}

        <button
          onClick={() => handleVerify()}
          disabled={verifying || code.join('').length !== 6}
          className="w-full py-3.5 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
          data-testid="verify-btn"
        >
          {verifying ? 'Verifying...' : <><span>Verify Email</span><ArrowRight className="w-4 h-4" /></>}
        </button>

        <div className="flex items-center justify-between mt-6">
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            data-testid="resend-code-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
            {resent ? 'Code sent!' : resending ? 'Sending...' : 'Resend code'}
          </button>
          {onBack && (
            <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">
              Use a different email
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
